import { useGetApiV1BoardsInfinite } from '@/open-api/boards/boards';
import type { NullableOfBoardSortOrder } from '@/open-api/model';
import { boardsKeys } from '@/utils/queryKeys';
import { useMemo } from 'react';

interface useGetBoardsInfiniteParams {
  boardSortOrder: NullableOfBoardSortOrder;
  userId?: string | number;
  userIsLoggedIn?: boolean;
}

const DEFAULT_PAGE_SIZE = 24;

export type useInfiniteGetBoardsReturnType = ReturnType<
  typeof useInfiniteGetBoards
>;

export const useInfiniteGetBoards = ({
  boardSortOrder = 'Latest',
  userIsLoggedIn,
  userId,
}: useGetBoardsInfiniteParams) => {
  const infiniteQueryResult = useGetApiV1BoardsInfinite(
    {
      PageNumber: 1,
      PageSize: DEFAULT_PAGE_SIZE,
      SortBy: boardSortOrder,
    },
    {
      query: {
        queryKey: [...boardsKeys.boards(userId), boardSortOrder],
        enabled: !!userIsLoggedIn,
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage?.boards?.length === DEFAULT_PAGE_SIZE) {
            return allPages.length + 1;
          }
        },
        staleTime: 1000,
        retry: (failureCount) => {
          if (failureCount < 1) {
            return true;
          }

          return false;
        },
      },
    }
  );

  const allBoards = useMemo(
    () =>
      infiniteQueryResult.data?.pages.flatMap((page) => page?.boards ?? []) ??
      [],
    [infiniteQueryResult.data?.pages]
  );

  return {
    ...infiniteQueryResult,
    boards: allBoards,
  };
};
